// REISOR_DRIVER_MARKET_CYRILLIC_DISPLAY_V1
//
// Display-only normalization.
// Canonical backend data / provenance are not changed.

function caseShape(original, converted) {
  const letters = String(original || "")
    .replace(/[^A-Za-zÇĞİÖŞÜçğıöşü]/g, "");

  if (!letters) return converted;

  const hasCase =
    letters.toUpperCase() !==
    letters.toLowerCase();

  if (
    hasCase &&
    letters === letters.toUpperCase()
  ) {
    return converted.toUpperCase();
  }

  const first = letters.charAt(0);
  const rest = letters.slice(1);

  if (
    first === first.toUpperCase() &&
    first !== first.toLowerCase() &&
    rest === rest.toLowerCase()
  ) {
    return converted
      ? converted.charAt(0).toUpperCase() +
          converted.slice(1)
      : converted;
  }

  return converted;
}


function transliterateLatinWord(rawWord) {
  const original = String(rawWord || "");

  let s = original
    .replace(/[ʻ’‘ʼ`]/g, "'")
    .toLowerCase()
    .replace(/i\u0307/g, "i");

  const pairs = [
    ["o'", "ў"],
    ["g'", "ғ"],
    ["sh", "ш"],
    ["ch", "ч"],
    ["yo", "ё"],
    ["yu", "ю"],
    ["ya", "я"],
    ["ye", "е"],
    ["ts", "ц"],
    ["zh", "ж"],
  ];

  const single = {
    a: "а",
    b: "б",
    c: "к",
    d: "д",
    e: "е",
    f: "ф",
    g: "г",
    h: "ҳ",
    i: "и",
    j: "ж",
    k: "к",
    l: "л",
    m: "м",
    n: "н",
    o: "о",
    p: "п",
    q: "қ",
    r: "р",
    s: "с",
    t: "т",
    u: "у",
    v: "в",
    w: "в",
    x: "х",
    y: "й",
    z: "з",

    "ç": "ч",
    "ğ": "ғ",
    "ı": "и",
    "ö": "ё",
    "ş": "ш",
    "ü": "у",
  };

  let out = "";

  for (let i = 0; i < s.length; ) {
    let matched = false;

    for (const [latin, cyr] of pairs) {
      if (s.startsWith(latin, i)) {
        out += cyr;
        i += latin.length;
        matched = true;
        break;
      }
    }

    if (matched) continue;

    const ch = s.charAt(i);

    if (ch === "'") {
      i += 1;
      continue;
    }

    out += single[ch] ?? ch;
    i += 1;
  }

  return caseShape(
    original,
    out
  );
}


export function normalizeMarketLanguage(raw) {
  const value = String(raw || "ru")
    .trim()
    .toLowerCase();

  if (value.startsWith("tr")) return "tr";
  if (value.startsWith("uz")) return "uz";
  return "ru";
}


export function marketDisplayText(raw, language = "ru") {
  const value = String(raw || "")
    .normalize("NFKC");

  return normalizeMarketLanguage(language) === "tr"
    ? value
    : marketCyrlText(value);
}


export function marketCyrlText(raw) {
  const value = String(raw || "")
    .normalize("NFKC");

  return value.replace(
    /[A-Za-zÇĞİÖŞÜçğıöşü][A-Za-zÇĞİÖŞÜçğıöşüʻ’‘ʼ'`-]*/g,
    (word) =>
      transliterateLatinWord(word)
  );
}


function cleanMarketPlace(raw) {
  let value = String(raw || "")
    .normalize("NFKC")
    .trim();

  if (!value) return "";

  // Driver does not need administrative template noise:
  // Toshkent (poytaxt) -> Toshkent
  // Samarqand viloyati -> Samarqand
  value = value
    .replace(
      /\(\s*(?:poytaxt|пойтахт)\s*\)/gi,
      " "
    )
    .replace(
      /(^|[\s,.;:(])(?:viloyati|viloyat|вилояти|вилоят|poytaxt|пойтахт)(?=$|[\s,.;:)])/gi,
      "$1"
    )
    .replace(
      /\(\s*\)/g,
      " "
    )
    .replace(
      /\s+/g,
      " "
    )
    .replace(
      /\s+([,.;:)])/g,
      "$1"
    )
    .replace(
      /([(])\s+/g,
      "$1"
    )
    .trim()
    .replace(
      /^[,.;:\-\s]+|[,.;:\-\s]+$/g,
      ""
    );

  return value;
}


export function marketCyrlPlace(raw) {
  return marketCyrlText(
    cleanMarketPlace(raw)
  );
}


const MARKET_CANONICAL_PLACE_CYR = Object.freeze({
  TASHKENT: "ТАШКЕНТ",
  ANDIJAN: "АНДИЖАН",
  FERGANA: "ФЕРГАНА",
  KOKAND: "КОКАНД",
  NAMANGAN: "НАМАНГАН",
  SAMARKAND: "САМАРКАНД",
  BUKHARA: "БУХАРА",
  KARSHI: "КАРШИ",
  TERMEZ: "ТЕРМЕЗ",
  NAVOI: "НАВОИ",
  URGENCH: "УРГЕНЧ",
  NUKUS: "НУКУС",
  JIZZAKH: "ДЖИЗАК",
  GULISTAN: "ГУЛИСТАН",

  MOSCOW: "МОСКВА",
  SAINT_PETERSBURG: "САНКТ-ПЕТЕРБУРГ",
  KAZAN: "КАЗАНЬ",
  UFA: "УФА",
  SAMARA: "САМАРА",
  SARATOV: "САРАТОВ",
  VOLGOGRAD: "ВОЛГОГРАД",
  ASTRAKHAN: "АСТРАХАНЬ",
  ROSTOV_ON_DON: "РОСТОВ-НА-ДОНУ",
  KRASNODAR: "КРАСНОДАР",
  STAVROPOL: "СТАВРОПОЛЬ",
  VORONEZH: "ВОРОНЕЖ",
  BELGOROD: "БЕЛГОРОД",
  KURSK: "КУРСК",
  LIPETSK: "ЛИПЕЦК",
  TAMBOV: "ТАМБОВ",
  PENZA: "ПЕНЗА",
  ULYANOVSK: "УЛЬЯНОВСК",
  NIZHNY_NOVGOROD: "НИЖНИЙ НОВГОРОД",
  ORENBURG: "ОРЕНБУРГ",
  ORSK: "ОРСК",
  EKATERINBURG: "ЕКАТЕРИНБУРГ",
  CHELYABINSK: "ЧЕЛЯБИНСК",
  TYUMEN: "ТЮМЕНЬ",
  OMSK: "ОМСК",
  NOVOSIBIRSK: "НОВОСИБИРСК",
  BARNAUL: "БАРНАУЛ",
  KRASNOYARSK: "КРАСНОЯРСК",
  IRKUTSK: "ИРКУТСК",
  PERM: "ПЕРМЬ",

  ALMATY: "АЛМАТЫ",
  ASTANA: "АСТАНА",
  SHYMKENT: "ШЫМКЕНТ",
  KARAGANDA: "КАРАГАНДА",
  TARAZ: "ТАРАЗ",
  TURKISTAN: "ТУРКЕСТАН",
  AKTOBE: "АКТОБЕ",
  AQTOBE: "АКТОБЕ",
  ATYRAU: "АТЫРАУ",
  AKTAU: "АКТАУ",

  BISHKEK: "БИШКЕК",
  OSH: "ОШ",
  JALAL_ABAD: "ДЖАЛАЛ-АБАД",

  DUSHANBE: "ДУШАНБЕ",
  KHUJAND: "ХУДЖАНД",
  ASHGABAT: "АШХАБАД",

  BAKU: "БАКУ",
  YEREVAN: "ЕРЕВАН",
  MINSK: "МИНСК",
  BREST: "БРЕСТ",
  CHISINAU: "КИШИНЁВ",
  TBILISI: "ТБИЛИСИ",
  BATUMI: "БАТУМИ",

  ISTANBUL: "СТАМБУЛ",
  ANKARA: "АНКАРА",
  IZMIR: "ИЗМИР",
  BURSA: "БУРСА",
  MERSIN: "МЕРСИН",
  ADANA: "АДАНА",
  GAZIANTEP: "ГАЗИАНТЕП",
  KONYA: "КОНЬЯ",
  ANTALYA: "АНТАЛЬЯ",

  WUQIA: "УЛУГЧАТ",
  IRKESHTAM: "ИРКЕШТАМ",
  KASHGAR: "КАШГАР",
  URUMQI: "УРУМЧИ",
  KHORGOS: "ХОРГОС",
  YINING: "КУЛЬДЖА",
  AKSU: "АКСУ",
  BEIJING: "ПЕКИН",
  SHANGHAI: "ШАНХАЙ",
  GUANGZHOU: "ГУАНЧЖОУ",
  SHENZHEN: "ШЭНЬЧЖЭНЬ",
  YIWU: "ИВУ",
});

const MARKET_PLACE_ALIAS_CYR = Object.freeze({
  "ТОШКЕН": "ТАШКЕНТ",
  "ТАШКЕН": "ТАШКЕНТ",
  "ТОШКЕНТ": "ТАШКЕНТ",

  "АНДЖОН": "АНДИЖАН",
  "АНДЖАН": "АНДИЖАН",
  "АНДИЖОН": "АНДИЖАН",

  "МОСКВУ": "МОСКВА",
  "МОСКВЫ": "МОСКВА",
  "МАСКВА": "МОСКВА",
  "МАСКВУ": "МОСКВА",
  "МОСКОВ": "МОСКВА",

  "ПИТЕР": "САНКТ-ПЕТЕРБУРГ",
  "СПБ": "САНКТ-ПЕТЕРБУРГ",

  "АЛМА АТА": "АЛМАТЫ",
  "АЛМАТУ": "АЛМАТЫ",

  "АҚТОБЕ": "АКТОБЕ",
  "ҚАШҒАР": "КАШГАР",
  "ҚАШҚАР": "КАШГАР",
});

function marketPlaceLookupKey(raw) {
  return marketCyrlText(
    cleanMarketPlace(raw)
  )
    .toLocaleUpperCase("ru-RU")
    .replace(/Ё/g, "Е")
    .replace(/[‐-‒–—-]+/g, " ")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function marketDisplayPlace(raw, language = "ru", canonical = "") {
  void language;

  const value = cleanMarketPlace(raw);
  if (!value && !canonical) return "";

  const canonicalKey = String(canonical || "")
    .trim()
    .toUpperCase();

  if (
    canonicalKey &&
    MARKET_CANONICAL_PLACE_CYR[canonicalKey]
  ) {
    return MARKET_CANONICAL_PLACE_CYR[canonicalKey];
  }

  const directLatinKey = value
    .toUpperCase()
    .replace(/[’‘ʼ`']/g, "")
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (
    directLatinKey &&
    MARKET_CANONICAL_PLACE_CYR[directLatinKey]
  ) {
    return MARKET_CANONICAL_PLACE_CYR[directLatinKey];
  }

  const aliasKey = marketPlaceLookupKey(value);

  if (
    aliasKey &&
    MARKET_PLACE_ALIAS_CYR[aliasKey]
  ) {
    return MARKET_PLACE_ALIAS_CYR[aliasKey];
  }

  return marketCyrlText(value)
    .toLocaleUpperCase("ru-RU");
}


/*
 * REISOR_STAGE133I_DRIVER_MARKET_DISPLAY_NORMALIZATION_V1
 *
 * Single display boundary for Driver Market.
 *
 * Canonical backend geography stays untouched.
 * Only the driver-facing representation is normalized.
 */
export function marketDriverDisplayItem(raw, language = "ru") {
  if (
    !raw ||
    typeof raw !== "object" ||
    Array.isArray(raw)
  ) {
    return raw;
  }

  return {
    ...raw,
    from: marketDisplayPlace(
      raw.from,
      language,
      raw.routeFromCanonical || raw.fromCanonical
    ),
    to: marketDisplayPlace(
      raw.to,
      language,
      raw.routeToCanonical || raw.toCanonical
    ),
  };
}

